/**
 * <load-more-list> — appends the next page of a collection in place.
 *
 * Rendered by snippets/load-more.liquid, which replaced {% render 'pagination' %} in
 * sections/main-collection-product-grid.liquid.
 *
 * Design constraints this file is built around:
 *
 *   1. It enhances a link, it does not replace one. The control is a real
 *      `<a href="{{ paginate.next.url }}">`. Nothing here runs until a click arrives, and
 *      the click is only cancelled once the element knows it can service it. If a fetch
 *      ever fails the element sets `this.failed` and stops cancelling clicks entirely, so
 *      the very next click is an ordinary navigation to page 2 - the same place Dawn's
 *      numbered pagination went.
 *
 *   2. It never composes copy. The count sentence, the fill width and the next-page URL
 *      are all read out of the fetched render, so wording, rounding and pagination logic
 *      stay in Liquid. Same trick assets/facets.js:152-183 uses for the product count.
 *
 *   3. It resets itself for free when a filter changes. assets/facets.js:139-150 replaces
 *      `#ProductGridContainer.innerHTML` wholesale on every filter and sort change, and
 *      this element renders inside that container. The old instance is discarded, the new
 *      markup is upgraded by the custom element registry as the parser inserts it, and
 *      `connectedCallback` starts again from the server's page-1 state - counters, track
 *      and next-page URL included. There is deliberately no reset() method: a second code
 *      path for the same state would be a second thing to keep in sync.
 *
 *      The one piece of state that outlives the container is the URL. `?page=N` is written
 *      with replaceState below; facets.js:335-337 then pushState's a URL built from the
 *      form data, which has no `page` key, so a filter change drops it without any
 *      cooperation needed here.
 *
 * Focus and announcement are both required, and do different jobs: focus moves a keyboard
 * user to the start of the batch they asked for (otherwise focus is on a button that has
 * just been removed, i.e. on <body>, and they are back at the top of the document), while
 * the `role="status"` count tells a screen reader user how much arrived.
 */
if (!customElements.get('load-more-list')) {
  customElements.define(
    'load-more-list',
    class LoadMoreList extends HTMLElement {
      connectedCallback() {
        this.grid = document.getElementById('product-grid');
        this.button = this.querySelector('[data-load-more-button]');
        this.countElement = this.querySelector('[data-load-more-count]');
        this.fill = this.querySelector('[data-load-more-fill]');
        this.loading = false;
        this.failed = false;

        if (!this.grid || !this.button) return;
        this.button.addEventListener('click', this.onClick.bind(this));
      }

      onClick(event) {
        // Two cases where the browser is left to follow the link: a fetch has already
        // failed once, or a request is still in flight and the shopper is impatient.
        if (this.failed) return;
        event.preventDefault();
        if (this.loading) return;

        const nextUrl = this.button.href;
        this.setLoading(true);

        fetch(this.sectionUrl(nextUrl))
          .then((response) => {
            if (!response.ok) throw new Error(`Load more: ${response.status} for ${nextUrl}`);
            return response.text();
          })
          .then((html) => {
            this.append(html, nextUrl);
            this.setLoading(false);
          })
          .catch((error) => {
            console.error(error);
            // Stand down rather than retry. The href is still page N+1, so the next click
            // is a full navigation that lands the shopper on the products they asked for.
            this.failed = true;
            this.setLoading(false);
          });
      }

      /**
       * `paginate.next.url` already carries the filter and sort params. Asking for it as a
       * section render returns the grid markup alone instead of a whole document, which is
       * the same shape assets/facets.js:51 fetches.
       */
      sectionUrl(href) {
        const url = new URL(href, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        return url.toString();
      }

      setLoading(isLoading) {
        this.loading = isLoading;
        if (!this.button) return;
        this.button.classList.toggle('load-more__button--loading', isLoading);
        // aria-disabled rather than removing href: the link must stay a link, because the
        // failure path above depends on the browser still being able to follow it. Set to
        // the literal "true" - toggleAttribute would leave `aria-disabled=""`, which ARIA
        // reads as false.
        if (isLoading) {
          this.button.setAttribute('aria-disabled', 'true');
        } else {
          this.button.removeAttribute('aria-disabled');
        }
      }

      append(html, nextUrl) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const sourceGrid = parsed.getElementById('product-grid');
        const sourceControl = parsed.querySelector('load-more-list');
        const items = sourceGrid ? Array.from(sourceGrid.children) : [];

        if (items.length === 0) {
          // Nothing came back - the collection changed under us, or the page is empty.
          // Drop the control instead of leaving a button that appends nothing.
          this.button.remove();
          this.button = null;
          return;
        }

        const firstNewItem = items[0];
        items.forEach((item) => this.grid.appendChild(item));

        // The appended cards carry `.scroll-trigger` when animations_reveal_on_scroll is on
        // (main-collection-product-grid.liquid:270). Without an observer they would sit at
        // the `opacity: 0.01` that base.css:3291-3294 gives them. Scoped to the grid rather
        // than the document so already-revealed sections are not re-observed. The whole
        // animation block is inside base.css's `prefers-reduced-motion: no-preference`
        // query, so this is a no-op for anyone who asked for less motion.
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(this.grid);
        }

        this.syncFromSource(sourceControl);
        this.rememberPage(nextUrl);

        // Focus last, after the count has been rewritten and before the button is dropped,
        // so focus never rests on an element that is about to be removed. `tabindex="-1"`
        // makes the card programmatically focusable without adding a tab stop.
        firstNewItem.setAttribute('tabindex', '-1');
        firstNewItem.focus();

        this.updateButton(sourceControl);
      }

      /** Copies the server's own count sentence and fill width onto the live control. */
      syncFromSource(sourceControl) {
        if (!sourceControl) return;

        const sourceCount = sourceControl.querySelector('[data-load-more-count]');
        if (sourceCount && this.countElement) this.countElement.textContent = sourceCount.textContent.trim();

        const sourceFill = sourceControl.querySelector('[data-load-more-fill]');
        if (sourceFill && this.fill) this.fill.style.width = sourceFill.style.width;

        if (sourceControl.dataset.loaded) this.dataset.loaded = sourceControl.dataset.loaded;
        if (sourceControl.dataset.total) this.dataset.total = sourceControl.dataset.total;
      }

      updateButton(sourceControl) {
        if (!this.button) return;
        const sourceButton = sourceControl && sourceControl.querySelector('[data-load-more-button]');

        if (!sourceButton) {
          // Last page: there is no page N+2 to offer.
          this.button.remove();
          this.button = null;
          return;
        }

        this.button.href = sourceButton.getAttribute('href');
      }

      /**
       * Keeps `?page=` in the address bar so a reload does not drop the shopper back to the
       * top of the collection. `history.state` is passed through untouched because
       * assets/facets.js:18-23 reads `event.state.searchParams` on popstate - replacing it
       * with null would make the back button re-render the page's initial filters instead
       * of the ones that entry actually described.
       */
      rememberPage(nextUrl) {
        const url = new URL(nextUrl, window.location.origin);
        url.searchParams.delete('section_id');
        history.replaceState(history.state, '', `${url.pathname}${url.search}`);
      }
    },
  );
}
